import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const DIAGNOSTIC_HIGHLIGHT_DURATION_MS = 1400
const PENDING_DIAGNOSTIC_FOCUS_KEY = "__navigationBuilderDiagnosticFocus"

// Connects to data-controller="admin--navigation-workspace"
export default class extends Controller {
  static targets = [
    "backdrop",
    "inspectorDrawer",
    "inspectorTrigger",
    "inspectorClose",
    "inspectorForm",
    "unsavedChangesDialog",
    "rollbackDialog",
    "rollbackTrigger",
    "liveRegion",
    "outlineRow",
    "pageTargetSelect",
    "pageSectionAnchorField",
    "pageSectionAnchorSelect",
    "pageSectionAnchorHint"
  ]

  static values = {
    inspectorOpen: Boolean,
    selectedItemId: Number,
    selectedLocation: String
  }

  connect() {
    this.pendingDirtyAction = null
    this.bypassDirtyGuardOnce = false
    this.dragClickSuppressedUntil = 0
    this.resetInspectorDirtyState()

    this.captureInspectorSnapshot()
    this.announceWorkspaceMessage()
    this.focusPendingDiagnostic()
    this.updatePageSectionAnchorOptions()

    if (this.hasSelectedItemIdValue && this.selectedItemIdValue > 0) this.applySelectedRowState(this.selectedItemIdValue)

    if (this.inspectorOpenValue) {
      this.openInspector()
    } else {
      this.updateBackdrop()
      this.updateBodyScrollLock()
    }
  }

  disconnect() {
    if (this.diagnosticPulseTimeout) {
      clearTimeout(this.diagnosticPulseTimeout)
      this.diagnosticPulseTimeout = null
    }

    document.body.classList.remove("overflow-hidden")
  }

  prepareForCache() {
    this.closeRollbackDialog()
    this.closeUnsavedChangesDialog()
    document.body.classList.remove("overflow-hidden")
  }

  handleSubmitEnd(event) {
    if (event?.detail?.success !== true) return

    if (this.isInspectorForm(event.target)) {
      this.captureInspectorSnapshot()
      if (this.consumeCloseInspectorOnSave(event.target)) this.closeInspector()
    }
  }

  trackInspectorFormChange(event) {
    const form = this.currentInspectorForm()
    if (!form || !form.contains(event.target)) return

    this.updateInspectorDirtyState()
  }

  guardNavigation(event) {
    if (Date.now() <= this.dragClickSuppressedUntil) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    if (event.currentTarget?.dataset?.navigationModeToggle) return

    if (this.bypassDirtyGuardOnce) {
      this.bypassDirtyGuardOnce = false
      return
    }

    if (!this.currentInspectorDirty()) return

    const url = event.currentTarget?.href
    if (!url) return

    event.preventDefault()
    this.openUnsavedChangesDialog({ type: "visit", url })
  }

  guardSubmit(event) {
    if (this.bypassDirtyGuardOnce) {
      this.bypassDirtyGuardOnce = false
      return
    }

    if (!this.currentInspectorDirty()) return
    if (this.isInspectorForm(event.target)) return

    event.preventDefault()
    this.openUnsavedChangesDialog({
      type: "submit",
      form: event.target,
      submitter: event.submitter || null
    })
  }

  prepareDiagnosticNavigation(event) {
    const source = event.currentTarget
    const itemId = Number(source?.dataset?.diagnosticFocusItemId || 0)
    if (!itemId) return

    this.persistPendingDiagnosticFocus(itemId)

    const href = source.href
    if (this.currentInspectorDirty() && href) {
      event.preventDefault()
      this.openUnsavedChangesDialog({ type: "visit", url: href })
      return
    }

    if (this.sameCurrentUrl(href)) {
      event.preventDefault()
      this.focusDiagnosticItem(itemId)
    }
  }

  toggleInspector(event) {
    if (event) event.preventDefault()

    if (this.inspectorOpen()) {
      this.requestInspectorClose()
    } else {
      this.openInspector()
    }
  }

  closeInspectorFromButton(event) {
    if (event) event.preventDefault()

    this.requestInspectorClose()
  }

  openRollbackDialog(event) {
    if (event) event.preventDefault()
    if (!this.hasRollbackDialogTarget) return

    this.rollbackDialogTarget.classList.remove("hidden")
    this.toggleButtonState(this.rollbackTriggerTargets, true)
    this.updateBackdrop()
    this.updateBodyScrollLock()

    const firstAction = this.rollbackDialogTarget.querySelector("button, [href], input, select, textarea")
    if (firstAction) firstAction.focus()
  }

  closeRollbackDialog(event) {
    if (event) event.preventDefault()
    if (!this.hasRollbackDialogTarget) return

    this.rollbackDialogTarget.classList.add("hidden")
    this.toggleButtonState(this.rollbackTriggerTargets, false)
    this.updateBackdrop()
    this.updateBodyScrollLock()
  }

  closeAll(event) {
    if (event && this.hasBackdropTarget && event.target !== this.backdropTarget) return

    if (this.rollbackDialogOpen()) this.closeRollbackDialog()
    this.requestInspectorClose()
  }

  handleEsc(event) {
    if (event.key !== "Escape") return

    if (this.unsavedChangesDialogOpen()) {
      this.cancelUnsavedInspectorChanges(event)
      return
    }

    if (this.rollbackDialogOpen()) {
      this.closeRollbackDialog(event)
      return
    }

    this.requestInspectorClose()
  }

  saveUnsavedInspectorChanges(event) {
    if (event) event.preventDefault()

    const form = this.currentInspectorForm()
    if (!form) {
      this.discardUnsavedInspectorChanges(event)
      return
    }

    if (typeof form.reportValidity === "function" && !form.reportValidity()) return

    this.closeUnsavedChangesDialog()
    this.applyCloseInspectorOnSave(form)
    form.requestSubmit()
  }

  discardUnsavedInspectorChanges(event) {
    if (event) event.preventDefault()

    const action = this.pendingDirtyAction
    this.pendingDirtyAction = null
    this.closeUnsavedChangesDialog()
    this.resetInspectorDirtyState()

    if (!action) {
      this.closeInspector()
      return
    }

    if (action.type === "visit" && action.url) {
      Turbo.visit(action.url)
      return
    }

    if (action.type === "submit" && action.form) {
      this.bypassDirtyGuardOnce = true
      action.form.requestSubmit(action.submitter || undefined)
      return
    }

    this.closeInspector()
  }

  cancelUnsavedInspectorChanges(event) {
    if (event) event.preventDefault()

    this.pendingDirtyAction = null
    this.closeUnsavedChangesDialog()
  }

  swallowEvent(event) {
    if (!event) return

    event.stopPropagation()
  }

  inspectorFormTargetConnected() {
    this.captureInspectorSnapshot()
  }

  suppressDragClick() {
    this.dragClickSuppressedUntil = Date.now() + 700
  }

  suppressClickAfterDrag(event) {
    if (Date.now() > this.dragClickSuppressedUntil) return

    event.preventDefault()
    event.stopPropagation()
  }

  openInspector() {
    if (this.hasInspectorDrawerTarget) this.inspectorDrawerTarget.classList.remove("translate-x-full")
    this.toggleButtonState(this.inspectorButtons(), true)
    this.updateBackdrop()
    this.updateBodyScrollLock()
  }

  requestInspectorClose() {
    if (!this.inspectorOpen()) return true

    if (!this.currentInspectorDirty()) {
      this.closeInspector()
      return true
    }

    this.openUnsavedChangesDialog()
    return false
  }

  closeInspector() {
    if (this.hasInspectorDrawerTarget) this.inspectorDrawerTarget.classList.add("translate-x-full")
    this.toggleButtonState(this.inspectorButtons(), false)
    this.resetInspectorDirtyState()
    this.updateBackdrop()
    this.updateBodyScrollLock()
  }

  inspectorOpen() {
    return this.hasInspectorDrawerTarget && !this.inspectorDrawerTarget.classList.contains("translate-x-full")
  }

  rollbackDialogOpen() {
    return this.hasRollbackDialogTarget && !this.rollbackDialogTarget.classList.contains("hidden")
  }

  updateBackdrop() {
    if (!this.hasBackdropTarget) return

    if (this.inspectorOpen() || this.rollbackDialogOpen()) {
      this.backdropTarget.classList.remove("hidden")
    } else {
      this.backdropTarget.classList.add("hidden")
    }
  }

  updateBodyScrollLock() {
    if (this.inspectorOpen() || this.rollbackDialogOpen() || this.unsavedChangesDialogOpen()) {
      document.body.classList.add("overflow-hidden")
    } else {
      document.body.classList.remove("overflow-hidden")
    }
  }

  openUnsavedChangesDialog(action = null) {
    this.pendingDirtyAction = action
    if (!this.hasUnsavedChangesDialogTarget) return

    this.unsavedChangesDialogTarget.classList.remove("hidden")
    const primaryActionButton = this.unsavedChangesDialogTarget.querySelector("[data-unsaved-action='save']")
    if (primaryActionButton) primaryActionButton.focus()
    this.updateBodyScrollLock()
  }

  closeUnsavedChangesDialog() {
    if (!this.hasUnsavedChangesDialogTarget) return

    this.unsavedChangesDialogTarget.classList.add("hidden")
    this.updateBodyScrollLock()
  }

  unsavedChangesDialogOpen() {
    return this.hasUnsavedChangesDialogTarget && !this.unsavedChangesDialogTarget.classList.contains("hidden")
  }

  currentInspectorDirty() {
    return this.updateInspectorDirtyState()
  }

  updateInspectorDirtyState() {
    const form = this.currentInspectorForm()
    if (!form) {
      this.resetInspectorDirtyState()
      return false
    }

    const snapshotKey = this.inspectorSnapshotKeyFor(form)
    if (this.inspectorSnapshot == null || this.inspectorSnapshotKey !== snapshotKey) {
      this.captureInspectorSnapshot()
    }

    this.inspectorDirty = this.serializeInspectorForm(form) !== this.inspectorSnapshot
    return this.inspectorDirty
  }

  captureInspectorSnapshot() {
    const form = this.currentInspectorForm()
    if (!form) {
      this.resetInspectorDirtyState()
      return
    }

    this.inspectorSnapshotKey = this.inspectorSnapshotKeyFor(form)
    this.inspectorSnapshot = this.serializeInspectorForm(form)
    this.inspectorDirty = false
  }

  resetInspectorDirtyState() {
    this.inspectorDirty = false
    this.inspectorSnapshot = null
    this.inspectorSnapshotKey = null
  }

  currentInspectorForm() {
    if (!this.hasInspectorFormTarget) return null
    return this.inspectorFormTarget
  }

  isInspectorForm(form) {
    return form instanceof HTMLFormElement && form.dataset.navigationWorkspaceInspectorForm === "true"
  }

  inspectorSnapshotKeyFor(form) {
    const action = form.getAttribute("action") || ""
    const methodOverride = form.querySelector("input[name='_method']")?.value || form.getAttribute("method") || "get"
    return `${action}|${String(methodOverride).toUpperCase()}`
  }

  serializeInspectorForm(form) {
    const payload = []
    const formData = new FormData(form)

    formData.forEach((value, key) => {
      if (["authenticity_token", "close_inspector_on_success"].includes(key)) return

      let normalizedValue
      if (value instanceof File) {
        const emptyUpload = value.name === "" && value.size === 0
        normalizedValue = emptyUpload ? "" : `file:${value.name}:${value.size}:${value.type}:${value.lastModified}`
      } else {
        normalizedValue = String(value)
      }
      payload.push([ key, normalizedValue ])
    })

    payload.sort((left, right) => {
      if (left[0] === right[0]) return left[1].localeCompare(right[1])
      return left[0].localeCompare(right[0])
    })

    return JSON.stringify(payload)
  }

  applyCloseInspectorOnSave(form) {
    let marker = form.querySelector("input[name='close_inspector_on_success']")
    if (!marker) {
      marker = document.createElement("input")
      marker.type = "hidden"
      marker.name = "close_inspector_on_success"
      form.appendChild(marker)
    }

    marker.value = "1"
  }

  consumeCloseInspectorOnSave(form) {
    if (!(form instanceof HTMLFormElement)) return false

    const marker = form.querySelector("input[name='close_inspector_on_success']")
    if (!marker) return false

    const shouldClose = marker.value === "1"
    marker.remove()
    return shouldClose
  }

  focusPendingDiagnostic() {
    const itemId = this.readPendingDiagnosticFocus()
    if (!itemId) return

    this.clearPendingDiagnosticFocus()
    this.focusDiagnosticItem(itemId)
  }

  focusDiagnosticItem(itemId) {
    this.openInspector()
    this.applySelectedRowState(itemId)

    const row = this.findOutlineRow(itemId)
    if (!row) return

    window.requestAnimationFrame(() => {
      row.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
      row.classList.add("navigation-builder-diagnostic-focus")
      if (this.diagnosticPulseTimeout) clearTimeout(this.diagnosticPulseTimeout)
      this.diagnosticPulseTimeout = setTimeout(() => {
        row.classList.remove("navigation-builder-diagnostic-focus")
      }, DIAGNOSTIC_HIGHLIGHT_DURATION_MS)
    })
  }

  applySelectedRowState(itemId) {
    this.outlineRowTargets.forEach((row) => {
      const selected = Number(row.dataset.itemId || 0) === Number(itemId)
      row.classList.toggle("navigation-builder-selected-row", selected)
    })
  }

  findOutlineRow(itemId) {
    return this.outlineRowTargets.find((row) => Number(row.dataset.itemId || 0) === Number(itemId)) || null
  }

  persistPendingDiagnosticFocus(itemId) {
    try {
      window.sessionStorage.setItem(PENDING_DIAGNOSTIC_FOCUS_KEY, String(itemId))
    } catch (_error) {
      window[PENDING_DIAGNOSTIC_FOCUS_KEY] = String(itemId)
    }
  }

  readPendingDiagnosticFocus() {
    try {
      return Number(window.sessionStorage.getItem(PENDING_DIAGNOSTIC_FOCUS_KEY) || 0)
    } catch (_error) {
      return Number(window[PENDING_DIAGNOSTIC_FOCUS_KEY] || 0)
    }
  }

  clearPendingDiagnosticFocus() {
    try {
      window.sessionStorage.removeItem(PENDING_DIAGNOSTIC_FOCUS_KEY)
    } catch (_error) {
      window[PENDING_DIAGNOSTIC_FOCUS_KEY] = null
    }
  }

  announceWorkspaceMessage() {
    if (!this.hasLiveRegionTarget) return

    const messageNode = this.element.querySelector("[data-navigation-workspace-notice], [data-navigation-workspace-alert]")
    if (messageNode?.textContent) {
      this.liveRegionTarget.textContent = messageNode.textContent.trim()
    }
  }

  updatePageSectionAnchorOptions() {
    if (!this.hasPageTargetSelectTarget || !this.hasPageSectionAnchorSelectTarget) return

    const optionsByPage = this.pageSectionOptionsByPage()
    const selectedPageId = String(this.pageTargetSelectTarget.value || "")
    const options = Array.isArray(optionsByPage[selectedPageId]) ? optionsByPage[selectedPageId] : []
    const select = this.pageSectionAnchorSelectTarget
    const previousValue = select.value

    select.innerHTML = ""
    select.append(this.buildAnchorOption("", "Top of page (no section anchor)", previousValue === ""))
    options.forEach((option) => {
      const key = String(option.key || "")
      if (!key) return

      select.append(this.buildAnchorOption(key, String(option.label || key), previousValue === key))
    })

    const pageSelected = selectedPageId.length > 0
    select.disabled = !pageSelected
    if (!pageSelected) select.value = ""

    if (this.hasPageSectionAnchorFieldTarget) {
      this.pageSectionAnchorFieldTarget.classList.toggle("opacity-60", !pageSelected)
    }

    if (this.hasPageSectionAnchorHintTarget) {
      if (!pageSelected) {
        this.pageSectionAnchorHintTarget.textContent = "Select a page first to choose a section anchor."
      } else if (options.length === 0) {
        this.pageSectionAnchorHintTarget.textContent = "This page has no navigation anchor sections yet."
      } else {
        this.pageSectionAnchorHintTarget.textContent = "Choose a section to append as a stable anchor fragment."
      }
    }
  }

  pageSectionOptionsByPage() {
    const raw = this.pageTargetSelectTarget.dataset.pageSectionOptions
    if (!raw) return {}

    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === "object" ? parsed : {}
    } catch (_error) {
      return {}
    }
  }

  buildAnchorOption(value, label, selected) {
    const option = document.createElement("option")
    option.value = value
    option.textContent = label
    option.selected = selected
    return option
  }

  toggleButtonState(buttons, active) {
    buttons.forEach((button) => {
      button.setAttribute("aria-expanded", active ? "true" : "false")
    })
  }

  inspectorButtons() {
    return [ ...this.inspectorTriggerTargets, ...this.inspectorCloseTargets ]
  }

  sameCurrentUrl(url) {
    if (!url) return false

    const current = new URL(window.location.href)
    const next = new URL(url, window.location.origin)
    current.hash = ""
    next.hash = ""
    return current.toString() === next.toString()
  }
}
